import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

interface UseAutosaveOptions<T> {
    data: T;
    onSave: (data: T) => Promise<void>;
    /** Debounce in ms (default 2000) */
    delay?: number;
    enabled?: boolean;
}

/**
 * Debounced autosave with status (ported from Scilora `use-autosave`). Changes are
 * detected via JSON.stringify of `data`; `saveNow()` flushes immediately (Ctrl+S).
 */
export function useAutosave<T>({ data, onSave, delay = 2000, enabled = true }: UseAutosaveOptions<T>) {
    const [status, setStatus] = useState<AutosaveStatus>('idle');
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const [, forceRender] = useState(0);
    const serialized = JSON.stringify(data);
    const lastSaved = useRef<string>(serialized);
    const latest = useRef<{ data: T; serialized: string }>({ data, serialized });
    latest.current = { data, serialized };
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlight = useRef(false);
    const delayRef = useRef(delay);
    delayRef.current = delay;

    const saveNow = useCallback(async () => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
        const { data: current, serialized: currentSerialized } = latest.current;
        if (currentSerialized === lastSaved.current || inFlight.current) return;
        inFlight.current = true;
        setStatus('saving');
        try {
            await onSaveRef.current(current);
            lastSaved.current = currentSerialized;
            setLastSavedAt(new Date());
            // Something changed while saving → stay "unsaved" so the next tick picks it up.
            setStatus(latest.current.serialized === currentSerialized ? 'saved' : 'unsaved');
        } catch {
            setStatus('error');
        } finally {
            inFlight.current = false;
        }
        // #526: a debounce that fired DURING the request was swallowed by the
        // in-flight guard and nothing re-armed it — the lesson stayed
        // "Ungespeicherte Änderungen" until the next keystroke. Re-arm here.
        if (latest.current.serialized !== lastSaved.current && !timer.current) {
            timer.current = setTimeout(() => {
                timer.current = null;
                void saveNow();
            }, delayRef.current);
        }
    }, []);

    useEffect(() => {
        if (!enabled) return;
        if (serialized === lastSaved.current) return;
        setStatus('unsaved');
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            void saveNow();
        }, delay);
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [serialized, enabled, delay, saveNow]);

    /** Marks the current data as persisted (e.g. after an external reload). */
    const markSaved = useCallback(() => {
        lastSaved.current = latest.current.serialized;
        setStatus('idle');
        forceRender((n) => n + 1); // isDirty is derived from the ref
    }, []);

    return { status, lastSavedAt, saveNow, markSaved, isDirty: serialized !== lastSaved.current };
}
